/**
 * SecuritySettings — account-level security management page.
 *
 * Path A 2FA Phase 2 (2026-05-26).
 *
 * Renders 2FA status + actions per state (Not enrolled / Preparation /
 * Enrolled). Phase 1 backend ships under svdy-authorization (id.svdy.com)
 * — see src/api/twofa.ts for the cross-service client.
 *
 * Future expansion (Phase 3+):
 *   - Sign-in challenge integration (verify TOTP between password +
 *     access_token issue)
 *   - Per-User session list (active devices, revoke)
 *   - Trusted device fingerprint (optional remember-me skip on 2FA)
 *
 * Page chrome follows §13 standard from svdy-ui (42px / 12px padding,
 * h6 600 weight title, 1px-border cards no shadow).
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import ShieldIcon from '@mui/icons-material/Shield';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { useTranslation } from 'react-i18next';

import { twoFactorApi, TwoFactorStatus } from '../../api/twofa';
import { ApiError } from '../../api/client';
import { translateApiError } from '../../i18n/translateApiError';
import TwoFactorSetupDialog from '../../components/security/TwoFactorSetupDialog';
import TwoFactorDisableDialog from '../../components/security/TwoFactorDisableDialog';
import RegenerateCodesDialog from '../../components/security/RegenerateCodesDialog';
import ChangePasswordDialog from '../../components/security/ChangePasswordDialog';
import { activeLocale } from '../../utils/datetime';

/**
 * Width of the settings column.
 *
 * A personal-security page is a form surface, so it is constrained rather than
 * stretched to the viewport — 1440px-wide prose is unreadable, which is why
 * GitHub, Google and Atlassian all cap theirs. 720 matches the worker portal's
 * own content cap so the shared component reads the same in both shells.
 */
const CONTENT_WIDTH = 720;

/** 10 codes are minted per batch — `_RECOVERY_CODE_COUNT` in
 *  svdy-authorization/app/auth/service_2fa.py. Shown as the denominator so the
 *  remaining figure reads as a fraction of a known whole rather than a bare number. */
const RECOVERY_CODE_TOTAL = 10;
/** Warn at three left. Chosen so the nudge arrives while the user still has
 *  several ways back in, not on the last one. */
const LOW_RECOVERY_CODES = 3;

/**
 * One card shape for both cards on this page.
 *
 * Written as a local component rather than inline twice so the icon tile, the
 * title row and the first content row share a baseline. Two hand-rolled headers
 * drift on exactly those three things (svdy-ui §11.3), and this page's two cards
 * sit directly above one another where any drift is obvious.
 */
const SecurityCard: React.FC<{
  icon: React.ReactNode;
  iconTone?: 'success' | 'neutral';
  title: string;
  description: string;
  status?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ icon, iconTone = 'neutral', title, description, status, children }) => (
  <Paper
    elevation={0}
    sx={{
      width: '100%',
      p: 3,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
    }}
  >
    <Stack direction="row" alignItems="flex-start" spacing={2}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: 1,
          bgcolor: iconTone === 'success' ? 'success.50' : 'background.default',
          border: '1px solid',
          borderColor: iconTone === 'success' ? 'success.200' : 'divider',
          color: iconTone === 'success' ? 'success.main' : 'text.secondary',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minHeight: 24 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
            {title}
          </Typography>
          {status}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {description}
        </Typography>
        {/* Only when there is something to show. An unconditional wrapper leaves
            16px of dead space between the description and the card's own 24px
            padding for any card without an action — the same "space against the
            frame" defect the password checklist had. */}
        {children ? <Box sx={{ mt: 2 }}>{children}</Box> : null}
      </Box>
    </Stack>
  </Paper>
);


function formatEnrolledAt(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(activeLocale(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const SecuritySettings: React.FC = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  // `?? 0` and not `?? RECOVERY_CODE_TOTAL`: an unreadable count must show as the
  // urgent case, never as a reassuring full house.
  const codesLeft = status?.recovery_codes_remaining ?? 0;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await twoFactorApi.status();
      setStatus(s);
    } catch (e) {
      setError(
        e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
      }}
    >
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, fontSize: '1.125rem', mb: 0.25 }}
      >
        {t('system:account.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('system:account.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ width: '100%', maxWidth: CONTENT_WIDTH, mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* A settings page, not a single card.
          It used to render ONE card in an otherwise empty viewport, which is what
          read as unbalanced — and the missing content was real, not decorative:
          changing your password is a personal security action that was reachable
          only from the avatar menu, so the page named "Security" did not contain
          it. Both cards go through one `SecurityCard` so their titles, icons and
          first content rows share a baseline (svdy-ui §11.3).

          Stacked rather than side by side: the 2FA card grows by three rows once
          enrolled, so a two-column row would need equal-height handling to avoid
          §11.2 — and every mainstream personal-security page (GitHub, Google,
          Atlassian) is a single constrained column anyway, because that is what
          keeps line length readable. */}
      <Stack spacing={2} sx={{ width: '100%', maxWidth: CONTENT_WIDTH }}>
        <SecurityCard
          icon={<KeyOutlinedIcon fontSize="small" />}
          title={t('system:account.passwordTitle')}
          description={t('system:account.passwordDescription')}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => setChangePasswordOpen(true)}
            sx={{ fontSize: '0.875rem' }}
          >
            {t('system:account.changePassword')}
          </Button>
        </SecurityCard>

        <SecurityCard
          icon={
            status?.enrolled
              ? <ShieldIcon fontSize="small" />
              : <ShieldOutlinedIcon fontSize="small" />
          }
          iconTone={status?.enrolled ? 'success' : 'neutral'}
          title={t('system:account.twoFactorTitle')}
          description={t('system:account.twoFactorDescription')}
          status={
            loading ? undefined : (
              <Chip
                label={
                  status?.enrolled
                    ? t('system:account.statusActive')
                    : status?.preparation
                      ? t('system:account.setupPending')
                      : t('system:account.statusOff')
                }
                size="small"
                variant="outlined"
                color={
                  status?.enrolled
                    ? 'success'
                    : status?.preparation ? 'warning' : 'default'
                }
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  borderRadius: 1,
                  ...(status?.enrolled || status?.preparation
                    ? {}
                    : { color: 'text.disabled' }),
                }}
              />
            )
          }
        >
          {loading ? (
            <Stack alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : status?.enrolled ? (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('system:account.enrolledLabel')}</strong>{' '}
                {formatEnrolledAt(status.enrolled_at)}
              </Typography>
              {/*
                How many recovery codes are left. `/2fa/status` has always returned
                `recovery_codes_remaining` and `api/twofa.ts` has always declared it,
                but nothing rendered it — so someone who had burned nine of ten codes
                saw exactly what someone who had used none saw, with a permanent
                lockout one code away. Each code is single-use
                (`router_2fa_challenge.py` removes the matched hash), and there is no
                operator path to clear MFA, so the number IS the warning.
              */}
              <Typography
                variant="body2"
                color={
                  codesLeft <= LOW_RECOVERY_CODES ? 'warning.main' : 'text.secondary'
                }
              >
                <strong>{t('system:account.recoveryCodesLabel')}</strong>{' '}
                {t('system:account.recoveryCodesValue', {
                  remaining: codesLeft,
                  total: RECOVERY_CODE_TOTAL,
                })}
              </Typography>
              {codesLeft <= LOW_RECOVERY_CODES && (
                <Alert severity="warning" sx={{ borderRadius: 1 }}>
                  {t('system:account.recoveryCodesLow')}
                </Alert>
              )}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setRegenerateOpen(true)}
                  sx={{ fontSize: '0.875rem' }}
                >
                  {t('system:account.regenerateCodes')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => setDisableOpen(true)}
                  sx={{ fontSize: '0.875rem' }}
                >
                  {t('system:account.disable2fa')}
                </Button>
              </Stack>
            </Stack>
          ) : status?.preparation ? (
            <Stack spacing={1.5}>
              <Alert severity="warning" sx={{ borderRadius: 1 }}>
                {t('system:account.setupIncomplete')}
              </Alert>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setSetupOpen(true)}
                  sx={{ fontSize: '0.875rem' }}
                >
                  {t('system:account.resumeSetup')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setDisableOpen(true)}
                  sx={{ fontSize: '0.875rem' }}
                >
                  {t('system:account.cancelSetup')}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={() => setSetupOpen(true)}
              sx={{ fontSize: '0.875rem' }}
            >
              {t('system:account.setup2fa')}
            </Button>
          )}
        </SecurityCard>
      </Stack>

      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      <TwoFactorSetupDialog
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        onEnrolled={() => {
          setSetupOpen(false);
          fetchStatus();
        }}
      />
      <TwoFactorDisableDialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        onDisabled={() => {
          setDisableOpen(false);
          fetchStatus();
        }}
      />
      <RegenerateCodesDialog
        open={regenerateOpen}
        onClose={() => setRegenerateOpen(false)}
        onRegenerated={() => setRegenerateOpen(false)}
      />
    </Box>
  );
};

export default SecuritySettings;
